import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import { getRules, putRules, type ScoringRules } from '../lib/admin';

const CODE = 'wc2026';

type Field = {
  key: keyof ScoringRules;
  label: string;
  hint: string;
  min: number;
  max: number;
};

// Order on the page mirrors the on-pitch story of a match: who wins → who
// scored what → by how much → the exact final. Then the boost knobs.
const FIELDS: Field[] = [
  {
    key: 'outcome',
    label: 'Outcome (1X2)',
    hint:
      'Points when the predicted winner — or a predicted draw — matches the actual match outcome, regardless of the exact score.',
    min: 0,
    max: 99,
  },
  {
    key: 'homeGoals',
    label: 'Home goals correct',
    hint:
      "Bonus when the player nailed the home team's goal count, even if the away score is wrong. Stacks with everything else.",
    min: 0,
    max: 99,
  },
  {
    key: 'awayGoals',
    label: 'Away goals correct',
    hint:
      "Bonus when the player nailed the away team's goal count. Independent of the home guess.",
    min: 0,
    max: 99,
  },
  {
    key: 'diff',
    label: 'Goal difference correct',
    hint:
      'Bonus when the predicted goal difference matches (e.g. predict 2-1, actual 3-2 — both home-by-one).',
    min: 0,
    max: 99,
  },
  {
    key: 'exact',
    label: 'Exact score',
    hint:
      "Top prize. Predicted both scores exactly. Stacks on top of outcome / goal-count / diff if those also fire (and they will).",
    min: 0,
    max: 99,
  },
  {
    key: 'boostMultiplier',
    label: 'Boost multiplier',
    hint:
      'Multiplier applied to the per-match total when the player spent a boost on it. 2 = doubles. Set 0 to disable boosting entirely. 1 is rejected (no-op).',
    min: 0,
    max: 9,
  },
  {
    key: 'boostTokens',
    label: 'Boost tokens per player',
    hint:
      'Maximum number of boosts a single player can spend across the whole tournament.',
    min: 0,
    max: 99,
  },
];

export function AdminRules() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [rules, setRules] = useState<ScoringRules | null>(null);
  const [draft, setDraft] = useState<Record<keyof ScoringRules, string>>({} as any);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function load() {
    try {
      const res = await getRules();
      setRules(res);
      setDraft({
        outcome: String(res.outcome),
        homeGoals: String(res.homeGoals),
        awayGoals: String(res.awayGoals),
        diff: String(res.diff),
        exact: String(res.exact),
        boostMultiplier: String(res.boostMultiplier),
        boostTokens: String(res.boostTokens),
      });
    } catch (e: any) {
      setErr(`${e?.message || e}`);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function update(key: keyof ScoringRules, value: string) {
    setDraft({ ...draft, [key]: value });
    setSaved(false);
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (!accessToken) return;
    setErr(null);
    setSaved(false);
    setSaving(true);
    try {
      const payload: Partial<ScoringRules> = {};
      for (const f of FIELDS) {
        const raw = draft[f.key];
        const n = parseInt(raw, 10);
        if (isNaN(n) || n < f.min || n > f.max) {
          throw new Error(`${f.label}: must be a whole number between ${f.min} and ${f.max}.`);
        }
        if (f.key === 'boostMultiplier' && n === 1) {
          throw new Error('Boost multiplier of 1 is the same as no boost. Use 0 to disable or ≥ 2.');
        }
        payload[f.key] = n;
      }
      const res = await putRules(accessToken, CODE, payload);
      setRules(res);
      setSaved(true);
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // Live preview of how a hypothetical "predict 2-1, actual 2-1, with boost"
  // match would score under the draft values. Mirrors scoring.Compute exactly.
  function previewPerfectMatch(): number {
    const v = (k: keyof ScoringRules) => {
      const n = parseInt(draft[k], 10);
      return isNaN(n) ? 0 : n;
    };
    let total = v('outcome') + v('homeGoals') + v('awayGoals') + v('diff') + v('exact');
    const m = v('boostMultiplier');
    if (m > 1 && total > 0) total *= m;
    return total;
  }

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Scoring rules — {CODE}</h1>
          </header>

          <p class="admin-lede">
            Fully additive. Every rule that triggers contributes its points; the per-match total is then multiplied by the boost if the player spent one. Setting a rule to <code>0</code> disables it. Changes apply to all future <em>match result</em> entries; matches already scored aren't retroactively rescored unless their result is re-saved on the Match results page.
          </p>

          {!rules && !err && <p class="admin-lede">Loading…</p>}

          {rules && (
            <form onSubmit={submit} class="admin-form">
              {FIELDS.map((f) => (
                <label key={f.key} class="admin-field">
                  <span>{f.label}</span>
                  <input
                    type="number"
                    min={f.min}
                    max={f.max}
                    value={draft[f.key] ?? ''}
                    onInput={(e) => update(f.key, (e.target as HTMLInputElement).value)}
                  />
                  <small style={{ color: 'rgba(245,237,224,0.55)', fontSize: 11, marginTop: 4 }}>
                    {f.hint}
                  </small>
                </label>
              ))}

              <dl class="admin-meta">
                <div>
                  <dt>Perfect match (predict 2-1, actual 2-1, with boost)</dt>
                  <dd>{previewPerfectMatch()} pts</dd>
                </div>
              </dl>

              {err && <p class="admin-error">{err}</p>}
              {saved && <p class="admin-saved">Saved.</p>}

              <button type="submit" class="admin-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}
